import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { listEvents } from '@/api/events'
import { usePolling } from '@/hooks/usePolling'
import type { EventItem } from '@/types'
import { formatKstDate } from '@/utils/datetime'
import EventBadge from './EventBadge'

const POLL_INTERVAL_MS = 30000
const MAX_ITEMS = 20
const STORAGE_KEY = 'alert_last_seen_id'

function getSince24h(): string {
  return new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString()
}

function formatTs(ts: string): string {
  const d = new Date(ts)
  const now = new Date()
  const diffMs = now.getTime() - d.getTime()
  const diffMin = Math.floor(diffMs / 60000)
  if (diffMin < 1) return 'just now'
  if (diffMin < 60) return `${diffMin}m ago`
  const diffH = Math.floor(diffMin / 60)
  if (diffH < 24) return `${diffH}h ago`
  return formatKstDate(d, '—')
}

// Shared state so both the icon and the panel stay in sync.
// Using a module-level subject pattern avoids prop drilling.
type Listener = (events: EventItem[]) => void
const listeners = new Set<Listener>()
let sharedEvents: EventItem[] = []

function notify(events: EventItem[]) {
  sharedEvents = events
  listeners.forEach((l) => l(events))
}

// Hook used by the header icon
export function useAlertEvents(): { events: EventItem[]; unreadCount: number } {
  const [events, setEvents] = useState<EventItem[]>(sharedEvents)
  useEffect(() => {
    listeners.add(setEvents)
    return () => { listeners.delete(setEvents) }
  }, [])
  const lastSeen = Number(localStorage.getItem(STORAGE_KEY) ?? 0)
  const unreadCount = events.filter((e) => e.id > lastSeen).length
  return { events, unreadCount }
}

interface Props {
  /** When true, renders as an inline section rather than a floating panel */
  inline?: boolean
}

export default function AlertPanel({ inline = false }: Props) {
  const [events, setEvents] = useState<EventItem[]>(sharedEvents)
  const [open, setOpen] = useState(false)
  const [lastSeen, setLastSeenState] = useState<number>(
    () => Number(localStorage.getItem(STORAGE_KEY) ?? 0),
  )
  const panelRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  // Subscribe to shared state
  useEffect(() => {
    listeners.add(setEvents)
    return () => { listeners.delete(setEvents) }
  }, [])

  const fetchAlerts = useCallback(async () => {
    try {
      const res = await listEvents({
        level: 'error,warning',
        since: getSince24h(),
        limit: MAX_ITEMS,
      })
      notify(res.items)
    } catch {
      // silently ignore — header badge just shows stale count
    }
  }, [])

  useEffect(() => { fetchAlerts() }, [fetchAlerts])
  usePolling(fetchAlerts, POLL_INTERVAL_MS)

  // Close panel on outside click
  useEffect(() => {
    if (!open) return
    function handle(e: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handle)
    return () => document.removeEventListener('mousedown', handle)
  }, [open])

  function markAllRead() {
    if (events.length === 0) return
    const maxId = Math.max(...events.map((e) => e.id))
    localStorage.setItem(STORAGE_KEY, String(maxId))
    setLastSeenState(maxId)
  }

  function handleOpen() {
    setOpen((v) => !v)
    markAllRead()
  }

  const unreadCount = events.filter((e) => e.id > lastSeen).length

  function handleRowClick(event: EventItem) {
    setOpen(false)
    if (event.node_id) {
      navigate(`/nodes/${event.node_id}`)
    }
  }

  if (inline) {
    return <AlertInline events={events} onRowClick={handleRowClick} />
  }

  return (
    <div style={{ position: 'relative' }} ref={panelRef}>
      {/* Bell icon button */}
      <button
        onClick={handleOpen}
        title="Alerts"
        aria-label={`${unreadCount} unread alerts`}
        style={{
          position: 'relative',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: '4px 8px',
          borderRadius: 4,
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          color: unreadCount > 0 ? '#c62828' : '#9090b0',
          fontFamily: 'inherit',
          fontSize: 13,
        }}
      >
        <span style={{ fontSize: 16 }}>&#9888;</span>
        {unreadCount > 0 && (
          <span
            style={{
              position: 'absolute',
              top: 0,
              right: 0,
              minWidth: 16,
              height: 16,
              background: '#c62828',
              color: '#fff',
              borderRadius: 8,
              fontSize: 10,
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 3px',
            }}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown panel */}
      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            right: 0,
            width: 420,
            maxWidth: '95vw',
            background: '#fff',
            border: '1px solid #e0e0e0',
            borderRadius: 8,
            boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
            zIndex: 2000,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '10px 14px',
              borderBottom: '1px solid #e0e0e0',
              background: '#f8f8fa',
            }}
          >
            <span style={{ fontWeight: 700, fontSize: 13 }}>
              Recent Alerts{' '}
              <span style={{ color: '#888', fontWeight: 400 }}>(last 24h)</span>
            </span>
            <button
              onClick={markAllRead}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: 11,
                color: '#3949ab',
                fontFamily: 'inherit',
              }}
            >
              Mark all read
            </button>
          </div>

          {events.length === 0 ? (
            <div style={{ padding: '20px 14px', color: '#888', fontSize: 13, textAlign: 'center' }}>
              No errors or warnings in the last 24 hours.
            </div>
          ) : (
            <div style={{ maxHeight: 360, overflowY: 'auto' }}>
              {events.map((ev) => (
                <AlertRow
                  key={ev.id}
                  event={ev}
                  isUnread={ev.id > lastSeen}
                  onClick={handleRowClick}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function AlertRow({
  event,
  isUnread,
  onClick,
}: {
  event: EventItem
  isUnread: boolean
  onClick: (e: EventItem) => void
}) {
  return (
    <div
      onClick={() => onClick(event)}
      style={{
        display: 'flex',
        gap: 10,
        padding: '9px 14px',
        borderBottom: '1px solid #f0f0f0',
        cursor: event.node_id ? 'pointer' : 'default',
        background: isUnread ? '#fffbf0' : '#fff',
        alignItems: 'flex-start',
      }}
    >
      <div style={{ paddingTop: 1 }}>
        <EventBadge level={event.level} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, color: '#222', marginBottom: 2, wordBreak: 'break-word' }}>
          {event.message}
        </div>
        <div style={{ fontSize: 11, color: '#888', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {event.node_id && (
            <span style={{ color: '#3949ab' }}>
              {event.node_id.slice(0, 8)}...
            </span>
          )}
          <span>{formatTs(event.created_at)}</span>
        </div>
      </div>
      {isUnread && (
        <div
          style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: '#f57f17',
            flexShrink: 0,
            marginTop: 5,
          }}
        />
      )}
    </div>
  )
}

// Inline variant for embedding in NodeListPage header area
function AlertInline({
  events,
  onRowClick,
}: {
  events: EventItem[]
  onRowClick: (e: EventItem) => void
}) {
  if (events.length === 0) return null
  return (
    <div
      style={{
        border: '1px solid #ffd54f',
        borderRadius: 6,
        background: '#fffde7',
        marginBottom: 20,
        overflow: 'hidden',
      }}
    >
      <div style={{ padding: '8px 14px', borderBottom: '1px solid #ffd54f', fontWeight: 600, fontSize: 13, color: '#e65100' }}>
        Active Alerts ({events.length})
      </div>
      {events.slice(0, 5).map((ev) => (
        <AlertRow key={ev.id} event={ev} isUnread={false} onClick={onRowClick} />
      ))}
    </div>
  )
}
